"use client";

import React, { useState, useMemo, useCallback } from "react";
import {
  Search, Plus, Edit3, Trash2, Tag, Layers, Table,
  LayoutList, CheckCircle, XCircle, RefreshCcw, X,
} from "lucide-react";
import ProductTypeModal from "./ProductTypeModal";
import { useProductTypes } from "../context/ProductTypeContext";
import Pagination, { usePagination } from "../shared/Pagination";

/* ─── Toggle Switch ─── */
const ToggleSwitch = ({ isActive, onToggle, id, status, disabled = false }) => (
  <label
    className={`relative inline-flex items-center ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
    title={isActive ? "Set Inactive" : "Set Active"}
  >
    <input
      type="checkbox"
      checked={isActive}
      onChange={() => !disabled && onToggle(id, status)}
      disabled={disabled}
      className="sr-only peer"
    />
    <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-sm" />
    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
  </label>
);

export default function ProductTypesPage() {
  const {
    productTypes,
    addProductType,
    updateProductType,
    deleteProductType,
    toggleStatus,
  } = useProductTypes();

  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy]             = useState("name");
  const [viewMode, setViewMode]         = useState("list");
  const [isModalOpen, setModalOpen]     = useState(false);
  const [editingType, setEditingType]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast]               = useState({ visible: false, message: "", type: "success" });

  const showToast = useCallback((message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  }, []);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const totalCount    = productTypes.length;
  const activeCount   = productTypes.filter(t => t.status === "Active").length;
  const inactiveCount = totalCount - activeCount;

  // ── Filtered + sorted ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return productTypes
      .filter(t => {
        const matchSearch = !search || (t.type_name || "").toLowerCase().includes(search.toLowerCase());
        const matchStatus = statusFilter === "all" || t.status === statusFilter;
        return matchSearch && matchStatus;
      })
      .sort((a, b) => {
        if (sortBy === "name")      return (a.type_name || "").localeCompare(b.type_name || "");
        if (sortBy === "name-desc") return (b.type_name || "").localeCompare(a.type_name || "");
        return 0;
      });
  }, [productTypes, search, statusFilter, sortBy]);

  const { page, pageSize, setPage, setPageSize, totalPages, totalItems, paginated }
    = usePagination(filtered, 10);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openAdd  = ()     => { setEditingType(null); setModalOpen(true); };
  const openEdit = type   => { setEditingType(type); setModalOpen(true); };

  const handleSave = async data => {
    try {
      editingType
        ? await updateProductType(editingType.id, data)
        : await addProductType(data);
      setModalOpen(false);
      showToast(editingType ? "Product type updated!" : "Product type created!");
    } catch { showToast("Failed to save.", "error"); }
  };

  const confirmDelete = async () => {
    try {
      await deleteProductType(deleteTarget.id);
      setDeleteTarget(null);
      showToast("Product type deleted.");
    } catch { showToast("Failed to delete.", "error"); }
  };

  const handleToggle = async (id, status) => {
    try {
      await toggleStatus(id, status);
      showToast(`Status updated to ${status === "Active" ? "Inactive" : "Active"}!`);
    } catch { showToast("Failed to update status.", "error"); }
  };

  const clearFilters = () => { setSearch(""); setStatusFilter("all"); setSortBy("name"); };
  const hasFilters = search || statusFilter !== "all" || sortBy !== "name";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Types", value: totalCount,    icon: LayoutList,  accent: "border-l-teal-500",  color: "text-teal-700",  bg: "bg-teal-50"  },
            { label: "Active",      value: activeCount,   icon: CheckCircle, accent: "border-l-green-500", color: "text-green-600", bg: "bg-green-50" },
            { label: "Inactive",    value: inactiveCount, icon: XCircle,     accent: "border-l-red-400",   color: "text-red-500",   bg: "bg-red-50"   },
          ].map(({ label, value, icon: Icon, accent, color, bg }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Search & Filter bar ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search product types…"
              className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
            />
            {search && (
              <button onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[140px]">
            <option value="all">All Status ({totalCount})</option>
            <option value="Active">Active ({activeCount})</option>
            <option value="Inactive">Inactive ({inactiveCount})</option>
          </select>

          {/* Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[130px]">
            <option value="name">Name A → Z</option>
            <option value="name-desc">Name Z → A</option>
          </select>

          {/* Reset */}
         {hasFilters && (
  <button onClick={clearFilters}
    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm">
    <RefreshCcw className="w-3.5 h-3.5 text-gray-500" /> Reset
  </button>
)}

          <div className="hidden sm:block w-px h-6 bg-gray-200 flex-shrink-0" />

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
            {[{ mode: "list", Icon: Layers }, { mode: "table", Icon: Table }].map(({ mode, Icon }) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`p-1.5 rounded-md transition-colors ${viewMode === mode ? "bg-white text-teal-600 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
                title={mode.charAt(0).toUpperCase() + mode.slice(1) + " view"}>
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Add button */}
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Product Type</span>
          </button>
        </div>

        {/* ── Results count ── */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing <span className="font-semibold text-gray-700">{filtered.length}</span> of{" "}
            <span className="font-semibold text-gray-700">{totalCount}</span> product types
          </span>
        </div>

        {/* ── Content ── */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl py-20 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Tag className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No product types found</h3>
            <p className="text-sm text-gray-400 mb-5">
              {hasFilters ? "Try adjusting your search or filters." : "Create your first product type to get started."}
            </p>
            {hasFilters ? (
              <button onClick={clearFilters}
                className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                Clear filters
              </button>
            ) : (
              <button onClick={openAdd}
                className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors">
                Add Product Type
              </button>
            )}
          </div>
        ) : (
          <>
            {/* ── List View ── */}
           {/* ── List View ── */}
{viewMode === "list" && (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
    <div className="space-y-2 p-3">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 py-3 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
        <span className="w-6 text-teal-200">ID</span>
        <span className="flex-1">Name</span>
        <span className="hidden sm:block w-24 text-center">Status</span>
        <span className="w-12 text-center">Toggle</span>
        <span className="w-16 text-right">Actions</span>
      </div>
      {paginated.map((type, i) => (
        <div key={type.id}
          className={`flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-4 py-3 transition-all duration-200 ${
            type.status !== "Active" ? "opacity-60" : "hover:shadow-sm hover:border-teal-200"
          }`}>
          <span className="text-xs text-gray-400 w-6 text-center flex-shrink-0">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{type.type_name || "Unnamed Type"}</p>
          </div>
          <span className={`hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full w-24 justify-center flex-shrink-0 ${
            type.status === "Active" ? "text-green-700 bg-green-50" : "text-red-500 bg-red-50"
          }`}>
            {type.status === "Active"
              ? <CheckCircle className="w-3 h-3" />
              : <XCircle className="w-3 h-3" />}
            {type.status}
          </span>
          <ToggleSwitch isActive={type.status === "Active"} onToggle={handleToggle} id={type.id} status={type.status} />
          <div className="flex items-center gap-1 flex-shrink-0 w-16 justify-end">
            <button onClick={() => openEdit(type)}
              className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setDeleteTarget(type)}
              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
    {/* <div className="px-4 border-t border-gray-100">
      <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
        pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
    </div> */}
  </div>
)}

            {/* ── Table View ── */}
            {viewMode === "table" && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead>
                      <tr className="bg-teal-700 text-white">
                        {["ID", "Name", "Status", "Actions"].map((h, i) => (
                          <th key={h} className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider ${
                            i === 3 ? "text-right" : i === 0 ? "text-center w-10" : "text-left"
                          }`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginated.map((type, i) => (
                        <tr key={type.id}
                          className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"} ${
                            type.status !== "Active" ? "opacity-60" : "hover:bg-teal-50/40"
                          }`}>
                          <td className="px-4 py-3 text-xs text-gray-400 text-center">{i + 1}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                             
                              <span className="font-semibold text-gray-800">{type.type_name || "Unnamed Type"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <ToggleSwitch isActive={type.status === "Active"} onToggle={handleToggle} id={type.id} status={type.status} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => openEdit(type)}
                                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setDeleteTarget(type)}
                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 border-t border-gray-100">
                  <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                    pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
                </div>
              </div>
            )}

            {/* Pagination for list */}
            {viewMode !== "table" && totalPages > 1 && (
              <div className="bg-white border border-gray-200 rounded-xl px-4 shadow-sm">
                <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                  pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      <ProductTypeModal
        isOpen={isModalOpen}
        onClose={() => { setModalOpen(false); setEditingType(null); }}
        onSave={handleSave}
        productType={editingType}
      />

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-[90%] max-w-sm p-6 z-10">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h2 className="text-base font-semibold text-gray-800 text-center mb-1">Delete Product Type</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-800">{deleteTarget?.type_name}</span>?{" "}
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors font-medium">
                Cancel
              </button>
              <button onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors font-semibold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      <div style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 9999,
        transition: "all 0.35s ease",
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.96)",
        pointerEvents: toast.visible ? "auto" : "none",
      }}>
        <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-semibold ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-teal-600 text-white"
        }`}>
          {toast.type === "error"
            ? <XCircle className="w-4 h-4 flex-shrink-0" />
            : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {toast.message}
        </div>
      </div>
    </div>
  );
}